/**
 * Free tools at /tools. Each one answers a search people make every week ("home depot
 * .03 meaning", "dollar general penny list this week", "ebay profit calculator") and
 * hands the reader to the app for the store-level answer.
 *
 * Same content rules as guides.ts: anything retailers don't publish is "commonly
 * reported", never policy. Plain strings only — both the React pages and the build-time
 * pre-renderer render these.
 */
import type { GuideSection } from "./guides";
import { SITE_URL, type JsonLd } from "./site";

export type ToolSlug =
  | "home-depot-price-decoder"
  | "dollar-general-penny-list"
  | "home-depot-penny-checker"
  | "dollar-general-coupon-calculator"
  | "resale-profit-calculator"
  | "target-clearance-decoder";

export interface Tool {
  slug: ToolSlug;
  /** <title> — keep under ~60 characters where possible. */
  title: string;
  h1: string;
  /** Short label for cards and nav. */
  navLabel: string;
  description: string;
  /** ISO date the content was last reviewed. */
  updated: string;
  /** Guide that covers the topic in depth, when there is one. */
  guidePath?: string;
  intro: string;
  sections: GuideSection[];
  faq: { q: string; a: string }[];
}

export const TOOLS: Tool[] = [
  {
    slug: "home-depot-price-decoder",
    title: "Home Depot Price Tag Decoder (.06, .03, .01 Meaning)",
    h1: "Home Depot price tag decoder",
    navLabel: "Home Depot price decoder",
    description:
      "Type in a Home Depot clearance price and see what the ending means — .06, .03 or a penny — how much you're saving, and what usually happens next.",
    updated: "2026-10-20",
    guidePath: "/guides/home-depot-penny-items",
    intro:
      "Home Depot doesn't publish its markdown codes, but clearance shoppers have tracked them for years. Enter the price on a yellow clearance tag and this decoder tells you what the cents commonly mean and whether the item is worth watching for a penny.",
    sections: [
      {
        heading: "What the price endings commonly mean",
        bullets: [
          ".06 — an earlier clearance markdown. More reductions are likely before the item is pulled.",
          ".03 — commonly treated as the final markdown. These are the items most likely to go to a penny next.",
          ".01 at the register — a penny item: Home Depot has flagged it for removal from the floor.",
          "Anything else (.00, .97, .98, .99 and so on) — not a recognized clearance ending. It's usually the regular or sale price.",
        ],
      },
      {
        heading: "How to use it in the store",
        paragraphs: [
          "Check the date printed on the yellow tag as well as the price. An item that has sat at .03 for a few weeks is the best penny candidate in the aisle. Scan it in the Home Depot app: if it shows no price or as unavailable while it's still on the shelf, it may already be marked for removal.",
          "These patterns come from shopper reports, vary by store and department, and aren't guaranteed. Treat them as hints, not rules.",
        ],
      },
    ],
    faq: [
      {
        q: "What does .03 mean at Home Depot?",
        a: "Clearance shoppers commonly report that a price ending in .03 is the final markdown before an item is pulled from the floor, which makes it the best candidate to go to a penny. Home Depot doesn't confirm this.",
      },
      {
        q: "What does .06 mean at Home Depot?",
        a: "A price ending in .06 is commonly reported as an earlier clearance markdown. The item will usually drop again — often to a .03 price — before it's removed.",
      },
      {
        q: "What does .01 mean at Home Depot?",
        a: "An item that rings up at $0.01 is a penny item: it's been flagged for removal. It won't be tagged at a penny, and the store can decline to sell it.",
      },
      {
        q: "Does a price ending in .00 or .97 mean clearance at Home Depot?",
        a: "Not usually. Those endings aren't among the clearance codes shoppers report for Home Depot; they're typically regular or sale prices.",
      },
    ],
  },
  {
    slug: "dollar-general-penny-list",
    title: "Dollar General Penny List This Week (Updated Daily)",
    h1: "Dollar General penny list this week",
    navLabel: "Dollar General penny list",
    description:
      "This week's Dollar General penny items, confirmed at $0.01 by Dollar General's own store price checks — not copied from rumor lists. Updated daily.",
    updated: "2026-10-20",
    guidePath: "/guides/dollar-general-penny-shopping",
    intro:
      "Every item on this list rang up at $0.01 in Dollar General's own store price check during DealHawk's daily crawl of hundreds of stores. New items are added as they're confirmed, newest first.",
    sections: [
      {
        heading: "How this list is built",
        paragraphs: [
          "Most penny lists repeat whatever someone posted in a group. This one doesn't: DealHawk checks Dollar General's store-level prices every day, and an item only lands here after Dollar General's own system returns $0.01 for it at a real store.",
          "Penny items differ by store. A confirmed penny at one store may still be full price, already pulled, or out of stock at yours, so treat the list as a shopping list to check, not a guarantee.",
        ],
      },
      {
        heading: "Tips for shopping the list",
        bullets: [
          "New Dollar General penny items are most often reported early in the week, with Tuesday the most-cited day.",
          "Check clearance end caps and seasonal leftovers first — that's where penny items usually sit.",
          "Scan items with the Dollar General app before you check out to confirm the price.",
          "Penny items aren't officially for sale. Be polite, don't ask staff to look items up, and accept a no.",
        ],
      },
    ],
    faq: [
      {
        q: "What day does Dollar General add penny items?",
        a: "Dollar General doesn't announce penny items. Shoppers most often report new ones early in the week, especially Tuesday. This list updates daily as items are confirmed.",
      },
      {
        q: "Are these Dollar General penny items confirmed?",
        a: "Yes — every item here returned $0.01 in Dollar General's own store price check during DealHawk's crawl. Prices still vary by store, so check at yours.",
      },
      {
        q: "Why are some UPCs partly hidden?",
        a: "The newest items show their full UPC. A DealHawk Dollar General plan shows the rest, plus which stores near you have them.",
      },
      {
        q: "Can Dollar General refuse to sell a penny item?",
        a: "Yes. Penny items are flagged for removal, so a store can decline the sale.",
      },
    ],
  },
  {
    slug: "home-depot-penny-checker",
    title: "Home Depot Penny Item Checker — Is My SKU a Penny?",
    h1: "Home Depot penny item checker",
    navLabel: "Home Depot penny checker",
    description:
      "Enter a Home Depot SKU or Internet number to see whether it's showing penny signals in DealHawk's store crawl and on community penny lists.",
    updated: "2026-10-20",
    guidePath: "/guides/home-depot-penny-items",
    intro:
      "Have a SKU from a penny list, or an item that's been sitting at .03? Check it here. DealHawk crawls Home Depot clearance at well over a thousand stores and flags items that look like they've hit the end of their markdown cycle.",
    sections: [
      {
        heading: "What the result means",
        bullets: [
          "Flagged by DealHawk — our crawl sees the item behaving like a penny candidate (clearance, still on hand, no longer sellable) at one or more stores.",
          "Reported on community lists — penny shoppers have posted it as ringing up at $0.01, with the states where it was found.",
          "Not flagged — we don't see penny signals for it right now. That doesn't mean it can't penny at your store.",
        ],
      },
      {
        heading: "Where to find the SKU",
        paragraphs: [
          "The store SKU is the 6- or 10-digit number on the shelf tag and in the Home Depot app (the app shows it as \"Store SKU\"). The 9-digit Internet # on homedepot.com works too.",
          "Want the exact stores, how many are on hand and the aisle? That's what DealHawk Penny Pro shows for the stores near you.",
        ],
      },
    ],
    faq: [
      {
        q: "How do I know if a Home Depot item is a penny?",
        a: "The only sure way is the register: penny items ring up at $0.01. Signals that it's close include a clearance price ending in .03, the app showing no price while it's on the shelf, and reports from other shoppers. This checker combines the last two.",
      },
      {
        q: "Is there an official Home Depot penny list?",
        a: "No. Home Depot doesn't publish one, and penny items differ by store. Community lists and store crawls like DealHawk's are the best signals available.",
      },
      {
        q: "Why is there a limit on lookups?",
        a: "To keep bots from scraping the checker. After a handful of lookups you may be asked to confirm you're human; a DealHawk account shows full penny lists for your stores without lookups.",
      },
    ],
  },
  {
    slug: "dollar-general-coupon-calculator",
    title: "Dollar General Coupon Calculator ($5 Off $25)",
    h1: "Dollar General coupon stacking calculator",
    navLabel: "DG coupon calculator",
    description:
      "Plan a Dollar General $5 off $25 trip: add items and digital coupons, see whether you hit the threshold, what you'll pay out of pocket and your cost after cash back.",
    updated: "2026-10-20",
    guidePath: "/guides/dollar-general-penny-shopping",
    intro:
      "Dollar General's spend-threshold coupons (like $5 off $25) are the backbone of most DG deal scenarios. Add your items and their digital coupons and this calculator shows whether your basket qualifies, how much more you'd need, and what each item really costs.",
    sections: [
      {
        heading: "How Dollar General coupon stacking works",
        bullets: [
          "Digital coupons are clipped in the Dollar General app and come off at the register when you enter your phone number.",
          "Spend-threshold coupons such as $5 off $25 apply to the whole basket. Their fine print typically says the spend is measured after other coupons and before tax — check the terms on yours.",
          "Usually only one digital coupon applies to each item, and a threshold coupon can be used once per transaction.",
          "Cash-back apps (Ibotta, Fetch and others) pay after the purchase, so they lower your net cost but not the register total.",
        ],
      },
      {
        heading: "Tips for building a basket",
        paragraphs: [
          "If you're a little under the threshold, add a cheap filler you'd buy anyway — or an item with its own coupon so the filler costs almost nothing. The calculator shows exactly how much you're short.",
        ],
      },
    ],
    faq: [
      {
        q: "Does the Dollar General $5 off $25 count before or after coupons?",
        a: "Dollar General's spend-threshold coupons typically say the $25 must be reached after other coupons are taken off and before tax. The calculator defaults to that; you can switch it if your coupon's terms say otherwise.",
      },
      {
        q: "What day is Dollar General $5 off $25?",
        a: "It's most often offered as a Saturday digital coupon, but Dollar General runs threshold coupons on other days and in other amounts too. Check the app's coupon tab.",
      },
      {
        q: "Can I use two digital coupons on one item at Dollar General?",
        a: "Generally no — one digital coupon per item. A spend-threshold coupon can still apply to the whole basket on top.",
      },
    ],
  },
  {
    slug: "resale-profit-calculator",
    title: "Resale Profit Calculator — eBay, Amazon, Mercari Fees",
    h1: "Resale profit calculator",
    navLabel: "Resale profit calculator",
    description:
      "See what you actually clear on a flip after eBay, Amazon, Mercari, Facebook or Poshmark fees, shipping and sales tax — plus your ROI and break-even price.",
    updated: "2026-10-20",
    intro:
      "Found something on clearance and wondering if it's worth flipping? Enter what you paid and what it sells for, pick a marketplace, and see your profit after fees and shipping, your return on investment, and the lowest price you can sell at without losing money.",
    sections: [
      {
        heading: "How the math works",
        bullets: [
          "Cost = what you paid plus sales tax.",
          "Marketplace fees = the platform's percentage of the sale (including shipping you charge, where the platform charges on it) plus any per-order fee.",
          "Profit = sale price + shipping charged − fees − your shipping cost − other costs − your cost.",
          "ROI = profit ÷ your cost. Break-even is the sale price where profit is exactly zero.",
        ],
      },
      {
        heading: "About the fee presets",
        paragraphs: [
          "Presets are approximate published rates for common categories. Marketplaces change fees and vary them by category, seller level and payment method, so edit the percentage and fixed fee to match your account before you rely on the number.",
        ],
      },
    ],
    faq: [
      {
        q: "How much does eBay take from a sale?",
        a: "For most categories eBay charges a final value fee of around 13–14% of the total sale (item plus shipping charged) plus a small per-order fee. Rates vary by category and seller status, so check eBay's fee page for yours.",
      },
      {
        q: "What's a good ROI for reselling?",
        a: "Many resellers look for at least 30–50% ROI on retail arbitrage to cover returns, unsold items and their time. Clearance and penny finds often run far higher.",
      },
      {
        q: "Should I include sales tax in my cost?",
        a: "Yes, unless you buy with a resale certificate. Tax is part of what you paid, and leaving it out overstates your profit.",
      },
    ],
  },
  {
    slug: "target-clearance-decoder",
    title: "Target Clearance Sticker Decoder & Markdown Calculator",
    h1: "Target clearance sticker decoder",
    navLabel: "Target clearance decoder",
    description:
      "Decode a Target clearance sticker: what the percent and price ending mean, the next markdown price, and which day that department is commonly marked down.",
    updated: "2026-10-20",
    guidePath: "/guides/target-clearance-schedule",
    intro:
      "Target clearance moves in predictable steps. Enter the original price and the percentage on the sticker, and this decoder shows what you're paying now, what the next markdowns would be, and the day shoppers commonly report that department gets marked down.",
    sections: [
      {
        heading: "How to read a Target clearance sticker",
        bullets: [
          "The number in the top-left corner of the red or orange sticker is the percent off the original price.",
          "Markdowns commonly step through 30%, 50% and 70% off, and some items — often seasonal — reach 90% before they're pulled.",
          "Price endings: shoppers commonly report that a clearance price ending in 4 means it's at its final markdown, while other endings may drop again. Target doesn't confirm this.",
        ],
      },
      {
        heading: "Should you wait for the next markdown?",
        paragraphs: [
          "Each step typically lasts a week or two. Popular items often sell out before the next markdown, so waiting is a bet: the decoder shows exactly how much you'd save by waiting so you can decide.",
        ],
      },
    ],
    faq: [
      {
        q: "What does the number on a Target clearance sticker mean?",
        a: "It's the percent off the original price — 30, 50, 70 or 90 on most clearance stickers.",
      },
      {
        q: "What does a Target price ending in 4 mean?",
        a: "Shoppers commonly report that a clearance price ending in 4 is the final markdown before the item is pulled. Target doesn't publish its pricing codes, so treat it as a hint.",
      },
      {
        q: "What day does Target mark down clearance?",
        a: "Shoppers commonly report a department-by-department schedule, for example electronics and baby on Monday, women's clothing on Tuesday, and toys and home décor on Thursday. Stores can differ.",
      },
    ],
  },
];

export const TOOLS_INDEX_SEO = {
  path: "/tools",
  title: "Free Clearance & Reseller Tools | DealHawk",
  description:
    "Free tools for clearance hunters and resellers: decode Home Depot and Target price tags, check Home Depot penny SKUs, this week's Dollar General penny list, coupon and resale profit calculators.",
};

export function getToolBySlug(slug: string): Tool | undefined {
  return TOOLS.find((t) => t.slug === slug);
}

export const toolPath = (slug: ToolSlug) => `/tools/${slug}`;

export function toolJsonLd(tool: Tool): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: tool.h1.charAt(0).toUpperCase() + tool.h1.slice(1),
    description: tool.description,
    url: `${SITE_URL}${toolPath(tool.slug)}`,
    applicationCategory: "UtilitiesApplication",
    operatingSystem: "Any",
    isAccessibleForFree: true,
    offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
    publisher: { "@type": "Organization", name: "DealHawk", url: SITE_URL },
  };
}
